import React, { useCallback } from 'react';
import isNumber from 'lodash/isNumber';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Percent } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Typography } from '@lyearn/ui';
import NumberInput from '@/components/NumberInput';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import { ResourceTypeEnum } from '@/types/schema';

import { emptyInputStyles, inputRootStyles } from './style';
import { getCertificateCondition } from './utils';

function CertificateCondition({
  contentType,
  error,
  minPercentage,
  onChange,
}: {
  contentType?: ResourceTypeEnum | 'EVENT';
  error?: boolean;
  minPercentage?: number | null;
  onChange: (value?: number) => void;
}) {
  const { t } = useTranslation();
  const [checked, { toggle }] = useToggle(() => isNumber(minPercentage));
  const certificateCondition = getCertificateCondition(t, contentType);

  const onToggle = useCallback(
    (value: boolean) => {
      if (value) {
        onChange(Number.NaN);
      } else {
        onChange(undefined);
      }
      toggle(value);
    },
    [onChange, toggle],
  );

  if (!certificateCondition) {
    return null;
  }

  const { id, label, description, placeholder } = certificateCondition;

  return (
    <Box>
      <SwitchWithInfo
        key={id}
        checked={checked}
        description={description}
        label={label}
        onChange={onToggle}
      />
      {checked && isNumber(minPercentage) ? (
        <>
          <NumberInput
            canBeEmpty
            className={cn('mt-16', inputRootStyles, emptyInputStyles)}
            endIcon={<Percent className="h-20 w-20" fillColorToken="icon-placeholder" />}
            error={!!error}
            max={100}
            min={0}
            placeholder={placeholder}
            showControls={false}
            value={minPercentage}
            onChange={onChange}
          />
          {error ? (
            <Typography className="mt-8 ms-16" color="text-text-error" variant="body-short-03">
              {t('Common:Form.Error.ValueCantBeEmpty')}
            </Typography>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}

export default CertificateCondition;
